import React, { useEffect, useState } from "react";
import api, { FOUNDATION } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/Layout";
import { Target, Eye, Landmark, Microscope, Users, ScrollText } from "lucide-react";

const DEFAULTS = {
  intro:
    `टूरिस्ट संदेश फाउंडेशन एक सांस्कृतिक, पर्यटन एवं शोध संस्था है जो उत्तराखंड एवं भारत की समृद्ध विरासत, परंपराओं एवं पर्यटन स्थलों के दस्तावेज़ीकरण, शोध एवं प्रचार-प्रसार हेतु समर्पित है। संस्था का उद्देश्य पर्यटन एवं संस्कृति के संदेश को जन-जन तक पहुँचाना है।`,
  history:
    `"टूरिस्ट संदेश" पत्रिका का प्रकाशन वर्ष ${FOUNDATION.magazineYear} में आरंभ हुआ। निरंतर प्रकाशन एवं सांस्कृतिक कार्यों के अनुभव के आधार पर वर्ष ${FOUNDATION.foundedYear} में टूरिस्ट संदेश फाउंडेशन की औपचारिक स्थापना की गई, ताकि पर्यटन, संस्कृति एवं विरासत के क्षेत्र में व्यवस्थित रूप से कार्य किया जा सके।`,
  objectives: [
    "पर्यटन, संस्कृति एवं विरासत से जुड़ी जानकारी का संकलन एवं प्रकाशन।",
    "उत्तराखंड एवं भारत के ऐतिहासिक एवं सांस्कृतिक स्थलों का दस्तावेज़ीकरण।",
    "शोध पत्रों, लेखों एवं पुस्तकों का प्रकाशन एवं संरक्षण।",
    "पर्यटन के प्रति जन-जागरूकता का प्रसार।",
    "सांस्कृतिक धरोहर के संरक्षण हेतु कार्यक्रम एवं पहल।",
  ],
  vision: "पर्यटन, संस्कृति एवं विरासत के ज्ञान का एक विश्वसनीय एवं समृद्ध डिजिटल भंडार तैयार करना।",
  mission: "शोध, प्रकाशन एवं दस्तावेज़ीकरण के माध्यम से हमारी सांस्कृतिक धरोहर को संरक्षित करना एवं आने वाली पीढ़ियों तक पहुँचाना।",
};

export default function About() {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/pages/about").then((res) => {
      if (res.data.content && Object.keys(res.data.content).length) {
        setContent({ ...DEFAULTS, ...res.data.content });
      }
    }).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader testId="about-header" title="हमारे बारे में" subtitle={`${FOUNDATION.nameEng} — पर्यटन, संस्कृति, विरासत एवं शोध की संस्था`} />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex flex-col items-center mb-8">
          <img src={FOUNDATION.logo} alt="logo" className="w-24 h-24 rounded-full object-cover ring-2 ring-amber-300 shadow-md" />
          <h2 className="font-display text-3xl text-[#0a2540] mt-4">{FOUNDATION.name}</h2>
        </div>
        <p className="prose-deva text-slate-700 text-center">{content.intro}</p>
      </section>

      <section className="bg-white border-y border-amber-200/50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8">
          <div className="bg-ivory border border-amber-200/60 rounded-xl p-7" style={{ background: "#faf9f5" }}>
            <div className="flex items-center gap-3 mb-3">
              <ScrollText className="text-amber-600" size={26} />
              <h3 className="font-display text-2xl text-[#0a2540]">फाउंडेशन का इतिहास</h3>
            </div>
            <p className="prose-deva text-slate-600 text-base">{content.history}</p>
          </div>
          <div className="bg-ivory border border-amber-200/60 rounded-xl p-7" style={{ background: "#faf9f5" }}>
            <div className="flex items-center gap-3 mb-3">
              <Users className="text-amber-600" size={26} />
              <h3 className="font-display text-2xl text-[#0a2540]">संस्थापक</h3>
            </div>
            <p className="prose-deva text-slate-600 text-base">
              फाउंडेशन के संस्थापक {FOUNDATION.founder} हैं, जिनके मार्गदर्शन में संस्था पर्यटन एवं
              सांस्कृतिक क्षेत्र में निरंतर कार्यरत है।
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <SectionTitle>हमारे उद्देश्य</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          {content.objectives.map((obj, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-amber-200/60 rounded-xl p-5 shadow-sm">
              <span className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-eng font-semibold shrink-0">{i + 1}</span>
              <p className="text-slate-700">{obj}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0a2540] py-16 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-7">
            <Eye className="text-amber-400 mb-3" size={30} />
            <h3 className="font-display text-2xl mb-2">हमारी दृष्टि (Vision)</h3>
            <p className="prose-deva text-slate-300 text-base">{content.vision}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-7">
            <Target className="text-amber-400 mb-3" size={30} />
            <h3 className="font-display text-2xl mb-2">हमारा ध्येय (Mission)</h3>
            <p className="prose-deva text-slate-300 text-base">{content.mission}</p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <SectionTitle sub="फाउंडेशन के कार्य के प्रमुख क्षेत्र">हमारे कार्यक्षेत्र</SectionTitle>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Landmark, t: "पर्यटन, संस्कृति एवं विरासत", d: "ऐतिहासिक एवं सांस्कृतिक धरोहर का संरक्षण एवं प्रचार।" },
            { icon: Microscope, t: "शोध एवं दस्तावेज़ीकरण", d: "गहन शोध, अध्ययन एवं प्रामाणिक दस्तावेज़ीकरण।" },
            { icon: ScrollText, t: "प्रकाशन एवं गतिविधियाँ", d: "पत्रिका, पुस्तकें, लेख एवं सांस्कृतिक कार्यक्रम।" },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-amber-200/60 rounded-xl p-6 text-center card-hover">
              <div className="w-14 h-14 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4">
                <c.icon size={26} />
              </div>
              <h3 className="font-serif-deva text-lg text-[#0a2540]">{c.t}</h3>
              <p className="text-sm text-slate-600 mt-2">{c.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
